import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ClientLanguageConfig } from '@app/core/models/client-language-config';
import {
  ENGLISH_LANGUAGE_CODE,
  Language,
} from '@app/core/models/language.model';
import {
  SmsCustomKeywordEntry,
  SmsKeywordType,
} from '@app/core/models/sms-custom-keyword';
import { SmsLanguageConfigService } from '@app/core/services/sms-language-config.service';
import { MessageDialogComponent } from '@app/shared/message-dialog/message-dialog.component';

export interface StopHelpTexts {
  stop: string;
  help: string;
}

/** Unsaved STOP/HELP edits by language code: only the fields that were edited. */
export type StopHelpEdits = Record<string, Partial<StopHelpTexts>>;

const FIELD: Record<SmsKeywordType, keyof StopHelpTexts> = {
  STOP: 'stop',
  HELP: 'help',
};

function savedText(
  language: ClientLanguageConfig | undefined,
  type: SmsKeywordType,
): string | null | undefined {
  return type === 'STOP'
    ? language?.stopResponseText
    : language?.helpResponseText;
}

/**
 * Client Inputs > Stop & Help Responses by language (DIS-1807).
 *
 * Adding or removing a language or a keyword saves immediately, because the
 * server validates it. STOP/HELP edits go to the page through `edits`, and
 * the page saves them with its Save button, so they survive the section being
 * collapsed. Only edited fields are saved.
 */
@Component({
  selector: 'rn-client-stop-help-languages',
  templateUrl: './client-stop-help-languages.component.html',
  styleUrls: ['./client-stop-help-languages.component.scss'],
  standalone: true,
  imports: [FormsModule, MessageDialogComponent],
})
export class ClientStopHelpLanguagesComponent implements OnChanges {
  @Input({ required: true }) clientId: string;
  @Input() canEdit = false;
  /** Unsaved STOP/HELP edits. Owned by the page; bind with `[(edits)]`. */
  @Input() edits: StopHelpEdits = {};
  @Output() editsChange = new EventEmitter<StopHelpEdits>();
  /** The client's SMS length limit, as for the old response boxes. */
  @Input() textLimit?: number;
  /**
   * Today's English text. While English has no text saved here, it starts as
   * an unsaved edit, so the next Save stores it.
   */
  @Input() englishFallback?: StopHelpTexts;

  @ViewChild('addLanguageDialog', { static: true })
  addLanguageDialog: MessageDialogComponent;
  @ViewChild('removeLanguageDialog', { static: true })
  removeLanguageDialog: MessageDialogComponent;
  @ViewChild('removeKeywordDialog', { static: true })
  removeKeywordDialog: MessageDialogComponent;
  @ViewChild('errorDialog', { static: true })
  errorDialog: MessageDialogComponent;

  readonly english = ENGLISH_LANGUAGE_CODE;
  readonly keywordTypes: SmsKeywordType[] = ['STOP', 'HELP'];

  library: Language[] = [];
  languages: ClientLanguageConfig[] = [];
  selectedCode = ENGLISH_LANGUAGE_CODE;
  keywordInput: Record<SmsKeywordType, string> = { STOP: '', HELP: '' };
  keywordError: Record<SmsKeywordType, string> = { STOP: '', HELP: '' };
  addingKeyword: Record<SmsKeywordType, boolean> = { STOP: false, HELP: false };
  languageToRemove: ClientLanguageConfig | null = null;
  keywordToRemove: SmsCustomKeywordEntry | null = null;
  loadFailed = false;

  constructor(private smsLanguageConfigService: SmsLanguageConfigService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId) {
      this.load();
    }
  }

  get selected(): ClientLanguageConfig | undefined {
    return (
      this.languages.find((l) => l.languageCode === this.selectedCode) ??
      this.languages[0]
    );
  }

  /** Library languages the client hasn't added yet. */
  get availableLanguages(): Language[] {
    return this.library.filter(
      (l) => !this.languages.some((c) => c.languageCode === l.code),
    );
  }

  name(code: string | undefined): string {
    return this.libraryLanguage(code)?.englishName ?? code ?? '';
  }

  /** Native name, when it differs from the English name. */
  nativeName(code: string): string {
    const language = this.libraryLanguage(code);
    return language && language.nativeName !== language.englishName
      ? language.nativeName
      : '';
  }

  dir(code: string): 'rtl' | 'ltr' {
    return this.libraryLanguage(code)?.direction === 'RTL' ? 'rtl' : 'ltr';
  }

  text(type: SmsKeywordType): string {
    const language = this.selected;
    return (
      this.edits[language?.languageCode]?.[FIELD[type]] ??
      savedText(language, type) ??
      ''
    );
  }

  /**
   * Whether any STOP/HELP text differs from what was there before editing.
   * English seeded from `englishFallback` doesn't count until it's edited:
   * it's seeded again on the next load, so leaving doesn't lose it.
   */
  get hasUnsavedChanges(): boolean {
    return Object.entries(this.edits).some(([code, edit]) => {
      const language = this.languages.find((l) => l.languageCode === code);
      return this.keywordTypes.some((type) => {
        const value = edit[FIELD[type]];
        const before =
          savedText(language, type) ??
          (code === ENGLISH_LANGUAGE_CODE
            ? this.englishFallback?.[FIELD[type]]
            : undefined) ??
          '';
        return value !== undefined && value !== before;
      });
    });
  }

  setText(type: SmsKeywordType, value: string): void {
    const code = this.selected.languageCode;
    this.setEdits({
      ...this.edits,
      [code]: { ...this.edits[code], [FIELD[type]]: value },
    });
  }

  systemKeywords(type: SmsKeywordType): string[] {
    return (this.selected?.systemKeywords ?? [])
      .filter((k) => k.type === type)
      .map((k) => k.keyword);
  }

  customKeywords(type: SmsKeywordType): SmsCustomKeywordEntry[] {
    return (this.selected?.customKeywords ?? []).filter((k) => k.type === type);
  }

  select(code: string): void {
    this.selectedCode = code;
    this.keywordInput = { STOP: '', HELP: '' };
    this.keywordError = { STOP: '', HELP: '' };
  }

  openAddLanguage(): void {
    this.addLanguageDialog.showMessage(null);
  }

  addLanguage(code: string): void {
    this.addLanguageDialog.close();
    this.smsLanguageConfigService
      .saveClientLanguages(this.clientId, [{ languageCode: code }])
      .subscribe({
        next: (languages) => {
          this.setLanguages(languages);
          this.select(code);
        },
        error: (err: HttpErrorResponse) =>
          this.showError('Could not add the language', err),
      });
  }

  confirmRemoveLanguage(language: ClientLanguageConfig): void {
    this.languageToRemove = language;
    this.removeLanguageDialog.showMessage(null);
  }

  removeLanguage(): void {
    const code = this.languageToRemove.languageCode;
    this.smsLanguageConfigService
      .removeLanguage(this.clientId, code)
      .subscribe({
        next: (languages) => {
          const edits = { ...this.edits };
          delete edits[code];
          this.setEdits(edits);
          this.setLanguages(languages);
          if (this.selectedCode === code) {
            this.select(ENGLISH_LANGUAGE_CODE);
          }
        },
        error: (err: HttpErrorResponse) =>
          this.showError('Could not remove the language', err),
      });
  }

  onKeywordInput(type: SmsKeywordType, value: string): void {
    this.keywordInput[type] = value;
    this.keywordError[type] = '';
  }

  onKeywordEnter(type: SmsKeywordType, event: KeyboardEvent): void {
    event.preventDefault();
    // Enter that commits IME input (e.g. Chinese) isn't a submit.
    if (!event.isComposing) {
      this.addKeyword(type);
    }
  }

  addKeyword(type: SmsKeywordType): void {
    const keyword = this.keywordInput[type].trim();
    const languageCode = this.selected?.languageCode;
    if (!keyword || !languageCode || this.addingKeyword[type]) {
      return;
    }
    this.addingKeyword[type] = true;
    this.smsLanguageConfigService
      .addCustomKeyword(this.clientId, languageCode, type, keyword)
      .subscribe({
        next: (created) => {
          this.addingKeyword[type] = false;
          const language = this.languages.find(
            (l) => l.languageCode === languageCode,
          );
          if (language) {
            language.customKeywords = [...language.customKeywords, created];
          }
          if (this.selectedCode === languageCode) {
            this.keywordInput[type] = '';
            this.keywordError[type] = '';
          }
        },
        error: (err: HttpErrorResponse) => {
          this.addingKeyword[type] = false;
          if (this.selectedCode === languageCode) {
            this.keywordError[type] =
              err.error?.reason ??
              'Could not add the keyword. Please try again.';
          }
        },
      });
  }

  confirmRemoveKeyword(keyword: SmsCustomKeywordEntry): void {
    this.keywordToRemove = keyword;
    this.removeKeywordDialog.showMessage(null);
  }

  removeKeyword(): void {
    const keyword = this.keywordToRemove;
    this.smsLanguageConfigService
      .removeCustomKeyword(this.clientId, keyword.languageCode, keyword.id)
      .subscribe({
        next: (languages) => this.setLanguages(languages),
        error: (err: HttpErrorResponse) =>
          this.showError('Could not remove the keyword', err),
      });
  }

  private load(): void {
    this.loadFailed = false;
    this.languages = [];
    this.selectedCode = ENGLISH_LANGUAGE_CODE;
    forkJoin({
      library: this.smsLanguageConfigService.getLanguageLibrary(this.clientId),
      languages: this.smsLanguageConfigService.getClientLanguages(
        this.clientId,
      ),
    }).subscribe({
      next: ({ library, languages }) => {
        this.library = library;
        this.setLanguages(languages);
      },
      error: () => {
        this.loadFailed = true;
      },
    });
  }

  private setLanguages(languages: ClientLanguageConfig[]): void {
    this.languages = languages;
    this.seedEnglishFallback();
  }

  private setEdits(edits: StopHelpEdits): void {
    this.edits = edits;
    this.editsChange.emit(edits);
  }

  /**
   * Clients created after the backfill have no English text here yet. Starting
   * from today's English text as an edit means the first Save makes English
   * Active instead of leaving it Incomplete.
   */
  private seedEnglishFallback(): void {
    const english = this.languages.find(
      (l) => l.languageCode === ENGLISH_LANGUAGE_CODE,
    );
    const edit = { ...this.edits[ENGLISH_LANGUAGE_CODE] };
    let seeded = false;
    if (
      english?.stopResponseText === null &&
      edit.stop === undefined &&
      this.englishFallback?.stop
    ) {
      edit.stop = this.englishFallback.stop;
      seeded = true;
    }
    if (
      english?.helpResponseText === null &&
      edit.help === undefined &&
      this.englishFallback?.help
    ) {
      edit.help = this.englishFallback.help;
      seeded = true;
    }
    if (seeded) {
      this.setEdits({ ...this.edits, [ENGLISH_LANGUAGE_CODE]: edit });
    }
  }

  private libraryLanguage(code: string | undefined): Language | undefined {
    return this.library.find((l) => l.code === code);
  }

  private showError(message: string, err: HttpErrorResponse): void {
    const reason = err.error?.reason;
    this.errorDialog.showMessage(
      reason ? `${message}: ${reason}` : `${message}. Please try again.`,
    );
  }
}
